import { AgGridReact } from 'ag-grid-react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, MenuItem, Select } from '@mui/material';
import { PropTypes } from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef, useContext } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Draggable from 'react-draggable';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorIcon from '@mui/icons-material/Error';
import Grid from '@mui/material/Grid';
import html2canvas from 'html2canvas';
import moment from 'moment';
import MuiCheckbox from '@mui/material/Checkbox';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircle from '@mui/icons-material/RemoveCircle';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';

import { colSpan, cellStyle, getEpicColors, getCellColorFromSkill } from './column';
import { MEMBER_LIST } from '../../apis/member';
import { PROJECT_WARNINGS, GET_DURATION_AND_START_DATE_OF_PROJECT } from '../../apis/project';
import { useAuth } from '../../context/AuthContext';
import * as timelineImg from '../NavBar/NavbarIcons';
import AgGridCell from './AgGridCell';
import Button from '../atoms/button';
import closeScreen from '../../images/closeScreen.svg';
import CustomTooltip from './customTooltip';
import exportIcon from '../../images/export.svg';
import LoadingOverlay from '../LoadingOverlay';
import openScreen from '../../images/openScreen.svg';
import ProjectContext from '../../context/ProjectContext';
import ProjectWarningContext from '../../context/ProjectWarningContext';
import refreshIcon from '../../images/refresh.svg';
import TeamMembersContext from '../../context/TeamMembersContext';
import TIMELINE_DATA from '../../apis/timeline';
import TimelineTourOptions from './TourOptions';
import useLoader from '../../context/LoaderContext';
import useProjectVersionHistory from '../../context/VersionHistoryContext';
import useWorkspace from '../../context/WorkspaceContext';

const StyledImage = styled.img`
  max-width: 465px;
  width: 100%;
`;

const CustomTypo = styled(Typography)`
  &.MuiTypography-root {
    font-style: normal;
    font-weight: 400;
    font-size: 20px;
    line-height: 32px;
    text-align: center;
    color: rgba(0, 0, 0, 0.56);
    max-width: 491px;
  }
`;

const HeaderColumn = styled.span``;

const headerWarningPropTypes = {
  i: PropTypes.number.isRequired,
};

const HeaderWarningComponent = ({ i }) => {
  const headerText = `Sprint - ${i}`;
  return (
    <span className="timeline-header">
      {i === 1 && <ErrorIcon id="project-not-started-error" fontSize="small" className="warning-icon" />}
      {headerText}
    </span>
  );
};

HeaderWarningComponent.propTypes = headerWarningPropTypes;

const headerPropTypes = {
  i: PropTypes.number.isRequired,
  sprintStartDate: PropTypes.string.isRequired,
  sprintDuration: PropTypes.number.isRequired,
};

const HeaderComponent = ({ i, sprintStartDate, sprintDuration }) => {
  const [formattedDate, setFormattedDate] = useState('');

  useEffect(() => {
    const calculateDate = () => {
      const daysToSum = (i - 1) * sprintDuration * 7;
      const sprintDate = moment(sprintStartDate).add(daysToSum, 'days');
      const newDate = sprintDate.format('D MMM');
      setFormattedDate(newDate);
    };

    calculateDate();
  }, [i, sprintStartDate, sprintDuration]);

  const headerText = `Sprint - ${i}`;

  return (
    <HeaderColumn className="ag-header-title">
      <div className="ag-timeline-header">{headerText}</div>
      <div className="date-label">{formattedDate}</div>
    </HeaderColumn>
  );
};

HeaderComponent.propTypes = headerPropTypes;

const CONTAINER_STYLE = { width: '100%', height: '100%' };
const GRID_STYLE = { height: '100%', width: '100%' };

const TimeLine = (project) => {
  // init
  const { id } = useParams();
  const { isLoading: isDataLoading, setIsLoading: setIsDataLoading } = useLoader();
  const { projectName } = project;
  const { duration: sprintDuration, startDate: sprintStartDate, dispatch } = useContext(ProjectContext);
  const { userPermissions: { roleId: userRoleId }, setTimelineTour, showTimelineTour } = useWorkspace();
  const { authUser: { id: userId } } = useAuth();
  const { dispatch: dispatchRiskBar } = useContext(ProjectWarningContext);
  const { dispatch: teamMemberDispatch } = useContext(TeamMembersContext);
  const { selectedListItem, showVersionHistoryBar, readOnlyMode, undo, redo } = useProjectVersionHistory();

  // states
  const [isLoading, setIsLoading] = useState(true);
  const [rowData, setRowData] = useState(undefined);
  const [removedRowsFromTimeline, setRemovedRowsFromTimeline] = useState({});
  const [forcePaintedRows, setForcePaintedRows] = useState([]);
  const [showRefreshButton, setShowRefreshButton] = useState(userRoleId === 1 || userRoleId === 2);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [exportStartSprint, setExportStartSprint] = useState(1);
  const [exportEndSprint, setExportSetEndSprint] = useState(null);
  const [exportSprint, setExportSetSprint] = useState(1);
  const [sprintDialogOpen, setSprintDialogOpen] = useState(false);
  const [showSkillLevelDetails, setShowSkillLevelDetails] = useState([true, true]);
  const [error, setError] = useState('');
  const [columnDefs, setColumnDefs] = useState([]);

  // constants
  const draggableRef = useRef(null);
  const gridRef = useRef(null);
  const isHidden = showVersionHistoryBar && readOnlyMode && selectedListItem !== null && selectedListItem !== undefined;
  const exportSprintOptions = columnDefs.filter((col) => col.field.startsWith('sprint'));
  const maxSprint = Math.max(...exportSprintOptions.map((col) => parseInt(col.field.replace('sprint', ''), 10)));
  const finalMaxSprint = maxSprint >= 50 ? 50 : maxSprint;
  const initialColumnDefs = [
    { headerName: 'Epic ID', field: 'displayEpicId', width: 130, pinned: 'left', cellClass: 'first-cell', lockPosition: 'left', headerClass: 'first-header-cell' },
    { headerName: 'Summary', suppressSizeToFit: true, resizable: true, field: 'summary', width: 160, pinned: 'left', cellClass: 'grey-text', lockPosition: 'left' },
  ];

  // mutations/queries
  const [getTimeline] = useLazyQuery(TIMELINE_DATA, { fetchPolicy: 'no-cache', variables: { input: { projectId: parseInt(id, 10) } } });
  const [getSprint] = useLazyQuery(GET_DURATION_AND_START_DATE_OF_PROJECT, { fetchPolicy: 'no-cache', variables: { input: { project_id: parseInt(id, 10) } } });
  const [getProjectWarnings] = useLazyQuery(PROJECT_WARNINGS, { fetchPolicy: 'no-cache', variables: { projectId: parseInt(id, 10) } });
  const [getProjectTeamMemberList] = useLazyQuery(MEMBER_LIST, { fetchPolicy: 'no-cache', variables: { projectId: parseInt(id, 10) } });

  const getProjectWarningsData = async () => {
    const { data } = await getProjectWarnings();
    await dispatchRiskBar({
      type: 'GET_PROJECT_WARNINGS',
      payload: {
        projectWarnings: data?.getProjectWarnings,
      },
    });
  };

  const setTimeLineData = async () => {
    if (!isDataLoading) setIsDataLoading(true);
    const { data: sprintData } = await getSprint();
    if (sprintData.sprint !== null) {
      const { start_date: startDate, duration } = sprintData.sprint;
      await dispatch({ type: 'SET_PROJECT_DETAIL', payload: { startDate, duration } });
    } else {
      await dispatch({ type: 'SET_PROJECT_DETAIL', payload: { startDate: null, duration: null } });
    }
    let message = '';
    let isProjectStarted = false;
    if (sprintDuration === null && sprintStartDate === null) {
      message = 'The project hasn’t started yet! Add a start date and sprint duration to get started.';
    } else if (sprintStartDate === null) {
      message = 'The project hasn’t started yet! Add a start date to get started.';
    } else if (sprintDuration === null) {
      message = 'The project hasn’t started yet! Set sprint duration in project settings to get started.';
    } else {
      isProjectStarted = true;
    }

    const { loading, data } = await getTimeline();

    if (!loading) {
      const timelineData = JSON.parse(data.getProjectTimeline.data);
      if (timelineData === null) {
        setIsDataLoading(false);
        setRowData([]);
        return;
      }
      const dynamicsColDefs = [];
      const colName = 'sprint';
      const noOfMaxCol = timelineData.maxCols;
      const { idealTime } = data.getProjectTimeline;
      const iterator = noOfMaxCol > idealTime ? noOfMaxCol + 1 : idealTime + 1;
      for (let i = 1; i <= iterator; i += 1) {
        const col = {
          headerName: `Sprint ${i}`,
          field: `${colName}${i}`,
          width: 110,
          cellStyle,
          headerTooltip: CustomTooltip,
          tooltipField: 'tooltipSummary',
          tooltipComponent: CustomTooltip,
          tooltipComponentParams: { noOfMaxCol, message },
          colSpan,
          lockPosition: 'left',
          headerComponentParams: { i },
          cellRenderer: AgGridCell,
        };
        if (isProjectStarted === false) {
          col.headerComponent = HeaderWarningComponent;
        } else {
          col.headerComponent = HeaderComponent;
          col.headerComponentParams = { i, sprintStartDate, sprintDuration };
        }
        dynamicsColDefs.push(col);
      }
      const updatedColumnDef = [...initialColumnDefs, ...dynamicsColDefs];
      setColumnDefs(updatedColumnDef);

      const rowTimelineData = [];
      let epicId = 0;
      let index = 0;
      timelineData.data.forEach((d, indx) => {
        if (epicId !== d.epicId && indx !== 0) {
          rowTimelineData.push({});
        }
        const filterData = d;
        filterData.tooltipSummary = filterData.summary;
        filterData.tooltipDisplayEpicId = filterData.displayEpicId;
        if (epicId === d.epicId) {
          filterData.summary = '';
          filterData.displayEpicId = '';
        }
        rowTimelineData.push({ id: index, ...filterData, ...JSON.parse(d.columns) });
        epicId = d.epicId;
        index += 1;
      });
      setRowData(rowTimelineData);
    }
    if (
      data !== null &&
      data.getProjectTimeline !== null &&
      data.getProjectTimeline.data !== null &&
      data.getProjectTimeline.idealTime !== null
    ) {
      const { idealTime } = data.getProjectTimeline;
      if (idealTime >= 0) {
        const left = 765 + 110 * idealTime - 797 + 30;
        if (document.getElementById('headerId') !== null) {
          const element = document.getElementById('customHeaderDivs');
          if (element) element.remove();
        }
        const setHeaderId = document.querySelector('div.ag-header-container');
        setHeaderId?.setAttribute('id', 'headerId');
        const newHeaderDiv = document.createElement('div');
        newHeaderDiv.setAttribute(
          'style',
          `position: absolute; border-left: 3px solid #000000; 
          left: ${`${left}px`}; height:100%; border-radius: 30px; display:block; cursor:pointer; z-index:1`,
        );
        newHeaderDiv.setAttribute('class', 'idealline');
        newHeaderDiv.setAttribute('id', 'customHeaderDivs');
        document.getElementById('headerId')?.appendChild(newHeaderDiv);
        const newSpansed = document.createElement('span');
        newSpansed.innerHTML = 'Ideal Line';
        newSpansed.setAttribute('class', 'tooltipideal');
        document.getElementById('customHeaderDivs')?.appendChild(newSpansed);

        if (document.getElementById('customDivs') !== null) {
          const elements = document.getElementById('customDivs');
          elements.remove();
        }
        const settId = document.querySelector('div.ag-center-cols-container');
        settId?.setAttribute('id', 'containerView');
        const newDivs = document.createElement('div');
        newDivs.setAttribute(
          'style',
          `position: absolute; border-left: 3px solid #000000;
            left: ${`${left}px`}; height:100%; border-radius: 30px; display:block; cursor:pointer; z-index:1`,
        );
        newDivs.setAttribute('class', 'idealline');
        newDivs.setAttribute('id', 'customDivs');
        document.getElementById('containerView')?.appendChild(newDivs);
        const newSpans = document.createElement('span');
        newSpans.innerHTML = 'Ideal Line';
        newSpans.setAttribute('class', 'tooltipideal');
        document.getElementById('customDivs')?.appendChild(newSpans);
      }
    } else {
      const headerElements = document.getElementById('customHeaderDivs');
      if (headerElements !== null) {
        headerElements.remove();
      }
      const bodyElements = document.getElementById('customDivs');
      if (bodyElements !== null) {
        bodyElements.remove();
      }
    }

    // here starts the today line code
    if (sprintData !== null && sprintData.sprint !== null && sprintData.sprint.start_date !== null && sprintData.sprint.duration !== null) {
      const currentDate = new Date();
      const sprintStartedDate = new Date(sprintData.sprint.start_date);
      const sprintEndDate = currentDate;
      const tv1 = sprintStartedDate.getTime();
      const tv2 = sprintEndDate.getTime();
      const nDays = (tv2 - tv1) / 1000 / 86400;
      const totalDays = Math.round(nDays);

      const setTodayLine = () => {
        const columnWidth = 108 / (sprintData.sprint.duration * 7);
        let leftValue = 0;
        leftValue = totalDays * columnWidth;
        // condition for today line at header
        if (document.getElementById('headerId') !== null) {
          const element = document.getElementById('customHeaderDiv');
          if (element !== null) {
            element.remove();
          }
        }
        const setHeaderId = document.querySelector('div.ag-header-container');
        setHeaderId?.setAttribute('id', 'headerId');
        const headerWidth = document.getElementById('headerId');
        if (leftValue <= headerWidth?.offsetWidth) {
          const newHeaderDiv = document.createElement('div');
          newHeaderDiv.setAttribute(
            'style',
            `position: absolute; border-left: 3px solid #98A2B3;
        left: ${`${leftValue}px`}; height:100%; border-radius: 30px; display:block; cursor:pointer; z-index:1`,
          );
          newHeaderDiv.setAttribute('class', 'todayline');
          newHeaderDiv.setAttribute('id', 'customHeaderDiv');
          document.getElementById('headerId')?.appendChild(newHeaderDiv);
          const newTooltipSpan = document.createElement('span');
          newTooltipSpan.innerHTML = 'Today';
          newTooltipSpan.setAttribute('class', 'tooltiptext');
          document.getElementById('customHeaderDiv')?.appendChild(newTooltipSpan);
        }

        // condition for today line at row
        if (document.getElementById('customDiv') !== null) {
          const element = document.getElementById('customDiv');
          element.remove();
        }
        if (leftValue <= headerWidth?.offsetWidth) {
          const setsId = document.querySelector('div.ag-center-cols-container');
          setsId?.setAttribute('id', 'containerView');
          const newDiv = document.createElement('div');
          newDiv.setAttribute(
            'style',
            `position: absolute; border-left: 3px solid #98A2B3;
        left: ${`${leftValue}px`}; height:100%; border-radius: 30px; display:block; cursor:pointer; z-index:1`,
          );
          newDiv.setAttribute('class', 'todayline');
          newDiv.setAttribute('id', 'customDiv');
          document.getElementById('containerView')?.appendChild(newDiv);
          const newSpan = document.createElement('span');
          newSpan.innerHTML = 'Today';
          newSpan.setAttribute('class', 'tooltiptext');
          document.getElementById('customDiv')?.appendChild(newSpan);
        }
        // today line code ends here
      };

      if (totalDays >= 0) {
        requestIdleCallback(() => setTodayLine());
      } else {
        const headerElement = document.getElementById('customHeaderDiv');
        if (headerElement !== null) {
          headerElement.remove();
        }
        const bodyElement = document.getElementById('customDiv');
        if (bodyElement !== null) {
          bodyElement.remove();
        }
      }
    } else {
      const headerElement = document.getElementById('customHeaderDiv');
      if (headerElement !== null) {
        headerElement.remove();
      }
      const bodyElement = document.getElementById('customDiv');
      if (bodyElement !== null) {
        bodyElement.remove();
      }
    }
    getProjectWarningsData();
    setIsDataLoading(false);
  };

  useEffect(() => {
    setTimeLineData();
  }, [sprintDuration, sprintStartDate, selectedListItem, undo, redo]);

  const openContextMenu = () => {
    const elements = document.getElementsByClassName('menu-icon');
    elements[0].click();
  };

  useEffect(() => {
    if (isDataLoading === false && showTimelineTour && rowData.length > 0) {
      const tourOptions = TimelineTourOptions({ openContextMenu });
      try {
        window.ProductTourJS.init(tourOptions);
        window.ProductTourJS.start();
        setTimelineTour(false);
      } catch (e) {
        console.error(e);
      }
    }
  }, [isDataLoading]);

  useEffect(() => {
    const refreshData = async () => {
      if (showRefreshButton === false) {
        const { data } = await getProjectTeamMemberList();
        if (data && data.getProjectSkillUsers.length > 0) {
          const userSkillData = data.getProjectSkillUsers.filter(
            (user) => user.user !== null && user.user.id === userId,
          );
          if (userSkillData.length > 0 && userSkillData[0].skillId === 2) {
            setShowRefreshButton(true);
          }
        }
      }
    };
    refreshData();
  }, []);

  useEffect(() => {
    if (exportSprintOptions && maxSprint) {
      setExportSetSprint(finalMaxSprint);
    }
  }, [exportEndSprint, maxSprint]);

  // return null if timeline yet not loaded
  if (!rowData) return null;

  async function waitForDataToLoad() {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), 100);
    });
  }

  const handleExport = async (startSprint, endSprint, exportShowStatus, exportShowTeamMembers) => {
    setIsLoading(true);
    const html = document.getElementsByTagName('html')[0];
    const gridContainer = document.querySelector('.ag-theme-alpine');
    const gridContent = document.querySelector('.ag-center-cols-container');
    gridContainer?.classList.add('export-mode');

    let htmlWidth = html.clientWidth;
    const containerWidth = gridContainer?.offsetWidth;
    const contentWidth = gridContent.scrollWidth;
    const newWidth = contentWidth - containerWidth;
    htmlWidth += newWidth;
    gridContainer.style.width = `${htmlWidth + 200}px`;

    const { columnApi, api } = gridRef.current;
    columnApi.getAllColumns().forEach((column, index) => {
      if (index < 2) {
        // Include "Epic ID" and "Summary" columns
        columnApi.setColumnVisible(column, true);
      } else {
        const headerText = column.getColDef().headerName;
        if (headerText.startsWith('Sprint')) {
          const sprintNumber = parseInt(headerText.split(' ')[1], 10);
          // Include specific sprint columns within the specified range
          if (sprintNumber >= startSprint && sprintNumber <= endSprint) {
            columnApi.setColumnVisible(column, true);
          } else {
            columnApi.setColumnVisible(column, false);
          }
        } else {
          // Hide all other columns
          columnApi.setColumnVisible(column, false);
        }
      }
    });

    if (gridContainer) {
      const allGridCells = document.querySelectorAll('.ag-cell');
      const visibleColumns = columnApi.getAllDisplayedColumns();
      const newWidths = visibleColumns.reduce((acc, column) => {
        const colDef = column.getColDef();
        return acc + (colDef.width || 100);
      }, 0);

      gridContainer.style.width = `${newWidths}px`;
      allGridCells.forEach((cell, columnIndex) => {
        if (columnIndex < 2) {
          // Keep the first two columns visible
          cell?.classList.remove('hidden-cell');
        } else {
          const headerText = cell.getAttribute('col-id');
          if (headerText.startsWith('sprint')) {
            const sprintNumber = parseInt(headerText.match(/\d+/)[0], 10);
            // Include specific sprint columns within the specified range
            if (sprintNumber >= startSprint && sprintNumber <= endSprint) {
              const skillText = cell.querySelector('.skillset-text');
              const timeLineLabel = cell.querySelector('.timeline-label');
              cell?.classList.remove('hidden-cell');
              cell.setAttribute('id', `${timeLineLabel.getAttribute('aria-rowindex')}${sprintNumber}`);
              if (skillText?.textContent) {
                cell.setAttribute('aria-rowindex', skillText.getAttribute('aria-rowindex'));
                cell.setAttribute('data-skill', skillText.getAttribute('data-skill'));
                cell.setAttribute('data-epic', skillText.getAttribute('data-epic'));
                cell?.classList.add('sprint-task');
              }
            } else {
              cell?.classList.add('hidden-cell');
            }
          } else {
            // Hide all other columns
            cell?.classList.add('hidden-cell');
          }
        }
      });

      const teamMemberElements = document.querySelectorAll('.user-assigned');
      const teamSkillElements = document.querySelectorAll('.skillset-text');
      const statusElements = document.querySelectorAll('.status-icon');
      const statusConditionIcon = document.querySelectorAll('.hamburg-icon');
      const sprintTask = document.querySelectorAll('.sprint-task');
      const projectNotStartedIcon = document.getElementById('project-not-started-error');

      if (exportShowStatus === false && exportShowTeamMembers === false) {
        if (projectNotStartedIcon) projectNotStartedIcon.style.setProperty('display', 'none');
        teamMemberElements.forEach((element) => element.style.setProperty('display', 'none'));
        teamSkillElements.forEach((element) => element.style.setProperty('display', 'none'));
        statusElements.forEach((element) => element.style.setProperty('display', 'none'));
        statusConditionIcon.forEach((element) => element.style.setProperty('display', 'none'));

        const distinctRows = new Set();
        api.forEachNode((row, index) => {
          if (Object.keys(row.data).length) {
            if (distinctRows.has(row.data.assignEpic)) {
              api.applyTransaction({ remove: [row.data] });
              setRemovedRowsFromTimeline((prev) => ({ ...prev, [index]: row.data }));
              return;
            }

            distinctRows.add(row.data.assignEpic);
            const epicColor = getEpicColors(row?.data?.summary || row?.data?.assignEpic);
            let rowTasks = [...sprintTask].filter((task) => task.getAttribute('data-epic') === row.data.assignEpic);

            let min = 0;
            let max = 0;
            rowTasks.forEach((task) => {
              const sprintNumber = parseInt(task.getAttribute('col-id').match(/\d+/)[0], 10);
              if (min === 0 || sprintNumber < min) min = sprintNumber;
              if (sprintNumber > max) max = sprintNumber;
            });

            const rowIndex = rowTasks[0].getAttribute('aria-rowindex');
            const sprintRange = [];
            for (let i = min; i <= max; i += 1) sprintRange.push(i);
            const consolidatedTasks = sprintRange.map((i) => {
              const task = document.getElementById(`${rowIndex}${i}`);
              if (!task) return null;
              if (task.getAttribute('data-skill') === null) setForcePaintedRows((prev) => [...prev, task]);
              return document.getElementById(`${rowIndex}${i}`);
            }).filter((task) => task !== null);
            rowTasks = consolidatedTasks;

            if (rowTasks.length >= 2) {
              rowTasks.forEach((task) => task.style.setProperty('border-radius', 0));
              rowTasks[0].style.setProperty('border-top-left-radius', '2px');
              rowTasks[0].style.setProperty('border-bottom-left-radius', '2px');
              rowTasks[rowTasks.length - 1].style.setProperty('border-top-right-radius', '2px');
              rowTasks[rowTasks.length - 1].style.setProperty('border-bottom-right-radius', '2px');
              rowTasks.forEach((task) => {
                task.style.setProperty('background-color', epicColor);
                task.style.setProperty('background-image', 'none');
              });
            } else {
              rowTasks.forEach((task) => {
                task.style.setProperty('background-color', epicColor);
                task.style.setProperty('background-image', 'none');
              });
            }
          }
        });
      } else {
        Object.entries(removedRowsFromTimeline).forEach(([index, data]) => {
          api.applyTransaction({ add: [data], addIndex: parseInt(index, 10) });
        });
        forcePaintedRows.forEach((task) => task.style.setProperty('background-color', '#ffffff'));
        setForcePaintedRows([]);
        setRemovedRowsFromTimeline({});
        if (projectNotStartedIcon) projectNotStartedIcon.style.setProperty('display', 'block');
        sprintTask.forEach((cell) => {
          cell?.classList.remove('sprint-task');
          cell.style.setProperty('border-radius', '2px');
          cell.style.setProperty('background-color', getCellColorFromSkill(cell.getAttribute('data-skill')));
        });

        teamMemberElements.forEach((element) => element.style.setProperty('display', 'flex'));
        teamSkillElements.forEach((element) => element.style.setProperty('display', 'flex'));
        teamSkillElements.forEach((element) => element.style.setProperty('color', '#ffffff'));

        if (exportShowStatus) {
          statusElements.forEach((element) => element.style.setProperty('display', 'flex'));
          statusConditionIcon.forEach((element) => element.style.setProperty('display', 'flex'));
        } else {
          statusElements.forEach((element) => element.style.setProperty('display', 'none'));
          statusConditionIcon.forEach((element) => element.style.setProperty('display', 'none'));
        }
      }

      await waitForDataToLoad();
      html2canvas(gridContainer).then((canvas) => {
        const image = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
        setImageData(image);
        setIsLoading(false);
      });
    }

    columnApi.getAllColumns().forEach((column) => {
      // Restore visibility for all columns
      columnApi.setColumnVisible(column, true);
    });

    html.style.width = null;
    gridContainer.style.width = null;
    gridContainer?.classList.remove('export-mode');
  };

  const handleExportChangeStartSprint = (event) => {
    setExportStartSprint(event.target.value);
    const sprintRange = Math.abs(event.target.value - (exportEndSprint !== null ? exportEndSprint : exportSprint));
    if (event.target.value > (exportEndSprint !== null ? exportEndSprint : exportSprint)) {
      setError('Start Sprint should be less than End Sprint');
      setImageData(null);
    } else if (event.target.value === (exportEndSprint !== null ? exportEndSprint : exportSprint)) {
      setError('Start Sprint and End Sprint should not be same');
      setImageData(null);
    } else if (sprintRange >= 50) {
      setError('Sprint range should not be greater than 50');
      setImageData(null);
    } else {
      setError('');
      handleExport(
        event.target.value,
        exportEndSprint !== null ? exportEndSprint : exportSprint,
        showSkillLevelDetails[0],
        showSkillLevelDetails[1],
      );
    }
  };

  const handleExportChangeEndSprint = (event) => {
    setExportSetEndSprint(event.target.value);
    const sprintRange = Math.abs(event.target.value - exportStartSprint);
    if (exportStartSprint > event.target.value) {
      setError('End Sprint should be greater than Start Sprint');
      setImageData(null);
    } else if (event.target.value === exportStartSprint) {
      setError('Start Sprint and End Sprint should not be same');
      setImageData(null);
    } else if (sprintRange >= 50) {
      setError('Sprint range should not be greater than 50');
      setImageData(null);
    } else {
      setError('');
      handleExport(exportStartSprint, event.target.value, showSkillLevelDetails[0], showSkillLevelDetails[1]);
    }
  };

  const calculateFormattedDate = (i, startDate, duration) => {
    let sprintDate;
    if (i === 1) {
      sprintDate = moment(startDate);
    } else {
      const daysToSum = (i - 1) * duration * 7;
      sprintDate = moment(startDate).add(daysToSum, 'days');
    }
    const formattedDate = sprintDate.format('D MMM, YY');
    return formattedDate;
  };

  const handleSprintExport = () => {
    setSprintDialogOpen(true);
    handleExport(exportStartSprint, exportSprint, showSkillLevelDetails[0], showSkillLevelDetails[1]);
  };

  const handleClose = async () => {
    setExportStartSprint(1);
    setExportSetEndSprint(null);
    setShowSkillLevelDetails([true, true]);
    setError('');

    Object.entries(removedRowsFromTimeline).forEach(([index, data]) => {
      gridRef.current.api.applyTransaction({ add: [data], addIndex: parseInt(index, 10) });
    });
    forcePaintedRows.forEach((task) => task.style.setProperty('background-color', '#ffffff'));
    const projectNotStartedIcon = document.getElementById('project-not-started-error');
    if (projectNotStartedIcon) projectNotStartedIcon.style.setProperty('display', 'block');
    setForcePaintedRows([]);
    setRemovedRowsFromTimeline({});
    gridRef.current.api.redrawRows();

    await teamMemberDispatch({
      type: 'SHOW_TEAM_MEMBERS',
      payload: {
        showTeamMembers: true,
      },
    });

    setSprintDialogOpen(false);
  };

  const handleExportImage = async () => {
    const link = document.createElement('a');
    const currentDate = moment(new Date()).format('YYYY-MM-DD');
    link.download = `${projectName}_simpleplan_${currentDate}.png`;
    link.href = imageData;
    document.body?.appendChild(link);
    link.click();
    document.body?.removeChild(link);
    setExportStartSprint(1);
    setExportSetEndSprint(null);
    setShowSkillLevelDetails([true, true]);
    setError('');
    Object.entries(removedRowsFromTimeline).forEach(([index, data]) => {
      gridRef.current.api.applyTransaction({ add: [data], addIndex: parseInt(index, 10) });
    });
    forcePaintedRows.forEach((task) => task.style.setProperty('background-color', '#ffffff'));
    setForcePaintedRows([]);
    setRemovedRowsFromTimeline({});
    gridRef.current.api.redrawRows();

    await teamMemberDispatch({
      type: 'SHOW_TEAM_MEMBERS',
      payload: {
        showTeamMembers: true,
      },
    });

    setSprintDialogOpen(false);
  };

  const handleShowStatus = async (e) => {
    if (!error) {
      setShowSkillLevelDetails([e.target.checked, showSkillLevelDetails[1]]);
      await teamMemberDispatch({
        type: 'SHOW_TEAM_MEMBERS',
        payload: {
          showTeamMembers: showSkillLevelDetails[1],
        },
      });
      handleExport(
        exportStartSprint,
        exportEndSprint !== null ? exportEndSprint : exportSprint,
        e.target.checked,
        showSkillLevelDetails[1],
      );
    }
  };

  const handleShowSkillLevelDetails = async (e) => {
    if (!error) {
      setShowSkillLevelDetails([e.target.checked, e.target.checked]);
      await teamMemberDispatch({
        type: 'SHOW_TEAM_MEMBERS',
        payload: {
          showTeamMembers: e.target.checked,
        },
      });
      handleExport(
        exportStartSprint,
        exportEndSprint !== null ? exportEndSprint : exportSprint,
        e.target.checked,
        e.target.checked,
      );
    }
  };

  const handleShowTeamMembers = async (e) => {
    if (!error) {
      setShowSkillLevelDetails([showSkillLevelDetails[0], e.target.checked]);
      await teamMemberDispatch({
        type: 'SHOW_TEAM_MEMBERS',
        payload: {
          showTeamMembers: e.target.checked,
        },
      });
      handleExport(
        exportStartSprint,
        exportEndSprint !== null ? exportEndSprint : exportSprint,
        showSkillLevelDetails[0],
        e.target.checked,
      );
    }
  };

  const handleOpenFullScreen = () => {
    setIsFullScreen(true);
    const targetElements = document.querySelectorAll('.MuiDrawer-root, .topbar-container, .tab-outer, .top-search-btn');
    const gridWidth = document.querySelector('.main-grid-item');
    const gridViewport = document.querySelector('.ag-body-viewport');
    if (targetElements) {
      if (gridViewport) {
        gridViewport.style.setProperty('max-height', 'calc(100vh - 53px)', 'important');
      }
      if (gridWidth) {
        gridWidth.style.setProperty('width', '100%');
      }
      targetElements.forEach((element) => {
        element.style.setProperty('display', 'none', 'important');
      });
    }
  };

  const handleCloseFullScreen = () => {
    setIsFullScreen(false);
    const targetElements = document.querySelectorAll('.MuiDrawer-root, .topbar-container, .tab-outer, .top-search-btn');
    const gridWidth = document.querySelector('.main-grid-item');
    const gridViewport = document.querySelector('.ag-body-viewport');

    if (targetElements) {
      if (gridViewport) {
        gridViewport.style.removeProperty('max-height');
      }
      if (gridWidth) {
        gridWidth.style.setProperty('width', 'calc(100% - 80px)');
      }
      targetElements.forEach((element) => {
        element.style.removeProperty('display');
      });
    }
  };

  const handleClick = (event) => {
    const clickedElementId = event.target.id;
    if (clickedElementId === 'exitFullscreenButton') {
      handleCloseFullScreen();
    }
  };

  window.addEventListener('popstate', () => {
    handleCloseFullScreen();
  });

  if (isDataLoading) return null;

  return (
    <div>
      {!rowData.length && !isDataLoading ? (
        <div>
          <Grid container style={{ padding: '28px' }}>
            <Grid
              item
              style={{ margin: '20px auto 0' }}
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              align="center"
            >
              <StyledImage src={timelineImg.notimeline} alt="noprojectart" draggable="false" />
            </Grid>
            <Grid
              item
              style={{ margin: '0 auto' }}
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              align="center"
            >
              <CustomTypo sx={{ mx: 'auto' }}>
                A timeline can&apos;t be generated yet. Please continue adding epics, skills and
                estimates.
              </CustomTypo>
            </Grid>
          </Grid>
        </div>
      ) : (
        <>
          <div className="page-header" style={{ padding: '10px 28px', alignItems: 'end' }}>
            <div style={{ display: isHidden ? 'none' : '' }}>
              <div className="alert-notify">
                {showRefreshButton && (
                  <div className="refresh-timeline-button header-layout">
                    <Button primary outlinedColor onClick={setTimeLineData}>
                      Refresh
                      <img src={refreshIcon} alt="refresh-icon" />
                    </Button>
                    <Button primary outlinedColor id="export-button" onClick={handleSprintExport}>
                      Export
                      <img src={exportIcon} alt="export-icon" />
                    </Button>
                    <Button primary outlinedColor onClick={handleOpenFullScreen}>
                      Full Screen
                      <img src={openScreen} alt="fullscreen-open" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={CONTAINER_STYLE} className="exportTheme">
            <div style={{ height: '100%', width: '100%', boxSizing: 'border-box' }}>
              <div style={GRID_STYLE} className="ag-theme-alpine">
                {isFullScreen && (
                  <div className="refresh-timeline-button timeline-full-screen">
                    <Draggable nodeRef={draggableRef}>
                      <Button
                        primary
                        outlinedColor
                        style={{ height: '33px' }}
                        disableRipple
                        onClick={handleClick}
                        id="exitFullscreenButton"
                        startIcon={<DragIndicatorIcon className="custom-drag-icon" />}
                        ref={draggableRef}
                      >
                        Exit Fullscreen
                        <img
                          src={closeScreen}
                          alt="fullscreen-open"
                          style={{ pointerEvents: 'none' }}
                        />
                      </Button>
                    </Draggable>
                  </div>
                )}
                <AgGridReact
                  className="ag-theme-custom-react-Timeline"
                  gridOptions={{
                    suppressNoRowsOverlay: true,
                    rowHeight: 20,
                    headerHeight: 20,
                    sideBar: true,
                  }}
                  rowBuffer
                  tooltipComponent={CustomTooltip}
                  tooltipHideDelay={5000}
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  domLayout="autoHeight"
                  animateRows
                />
              </div>
            </div>
          </div>
        </>
      )}
      <Grid container direction="row">
        <div>
          <Dialog open={sprintDialogOpen} className="export-sprint">
            <DialogTitle>Export Image</DialogTitle>
            <Grid container direction="row">
              <Grid id="export_set_sprint">
                <DialogContent style={{ width: '350px' }}>
                  <Typography><strong>Sprint Range</strong></Typography>
                  <FormControl fullWidth>
                    <Select
                      value={exportStartSprint}
                      onChange={handleExportChangeStartSprint}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            height: 'auto',
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {exportSprintOptions.map((sprint) => (
                        <MenuItem
                          key={sprint.headerComponentParams.i}
                          value={sprint.headerComponentParams.i}
                          className="export-select"
                        >
                          {sprint.headerComponentParams.sprintStartDate !== undefined &&
                            sprintDuration !== null
                            ? `${sprint.headerName}: Start Date - ${calculateFormattedDate(
                              sprint.headerComponentParams.i,
                              sprint.headerComponentParams.sprintStartDate,
                              sprintDuration,
                            )}`
                            : sprint.headerName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <Select
                      defaultValue={finalMaxSprint}
                      onChange={handleExportChangeEndSprint}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            height: 'auto',
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {exportSprintOptions.map((sprint) => (
                        <MenuItem
                          key={sprint.headerComponentParams.i}
                          value={sprint.headerComponentParams.i}
                          className="export-select"
                        >
                          {sprint.headerComponentParams.sprintStartDate !== undefined &&
                            sprintDuration !== null
                            ? `${sprint.headerName}: Start Date - ${calculateFormattedDate(
                              sprint.headerComponentParams.i,
                              sprint.headerComponentParams.sprintStartDate,
                              sprintDuration,
                            )}`
                            : sprint.headerName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {error && <div className="error-message">{error}</div>}
                  <Typography style={{ margin: '15px 0px 12px 0px' }}>
                    <strong>Enable/Disable</strong>
                  </Typography>
                  <div>
                    <FormControlLabel
                      label="Show skill-level detail"
                      control={<MuiCheckbox
                        indeterminateIcon={<RemoveCircle />}
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        indeterminate={showSkillLevelDetails[0] !== showSkillLevelDetails[1]}
                        checked={showSkillLevelDetails[0] && showSkillLevelDetails[1]}
                        onChange={handleShowSkillLevelDetails}
                      />}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                      <FormControlLabel
                        label="Show status"
                        control={
                          <MuiCheckbox
                            icon={<RadioButtonUncheckedIcon />}
                            checkedIcon={<CheckCircleIcon />}
                            onChange={handleShowStatus}
                            checked={showSkillLevelDetails[0]}
                          />
                        }
                      />
                      <FormControlLabel
                        label="Show assigned team members"
                        control={
                          <MuiCheckbox
                            icon={<RadioButtonUncheckedIcon />}
                            checkedIcon={<CheckCircleIcon />}
                            checked={showSkillLevelDetails[1]}
                            onChange={handleShowTeamMembers}
                          />
                        }
                      />
                    </Box>
                  </div>
                </DialogContent>
              </Grid>
              <Grid>
                <DialogContent className="export-preview-conent">
                  {isLoading ? (
                    <LoadingOverlay open={isLoading} />
                  ) : (
                    <img className="export-preview" src={imageData} alt=" " />
                  )}
                </DialogContent>
              </Grid>
            </Grid>
            <DialogActions>
              <Button primary outlined onClick={handleClose}>Cancel</Button>
              <Button primary className={`${error ? 'export-disabled' : ''}`} onClick={handleExportImage} disabled={!!error}>Export</Button>
            </DialogActions>
          </Dialog>
        </div>
      </Grid>
    </div>
  );
};

export default TimeLine;
